import React from "react";
import {
  Paper,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
} from "@mui/material";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import HeadsetMicOutlinedIcon from "@mui/icons-material/HeadsetMicOutlined";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../services/auth/AuthContext";

/**
 * UserQuickNav - Quick navigation for authenticated user actions
 * @param {Function} onItemClick - Optional callback when menu item is clicked
 * @returns {JSX.Element} RTL-supported navigation panel with wishlist & orders links
 */
export default function UserQuickNav({ onItemClick }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth();

  const primaryColor = "#d65a31"; // Matching the screenshot's warm orange/terracotta tone
  const bgColor = "#fffdf0"; // Warm cream background from screenshot

  const menuItems = [
    {
      id: "wishlist",
      label: "علاقه‌مندی‌ها",
      icon: <FavoriteBorderIcon />,
      path: "/wishlist",
      requiresAuth: true,
    },
    {
      id: "invoices",
      label: "سفارشات من",
      icon: <ShoppingBagOutlinedIcon />,
      path: "/invoices",
      requiresAuth: true,
    },
  ];

  const handleNav = (item) => {
    if (onItemClick) onItemClick(item);

    if (item.requiresAuth && !isAuthenticated) {
      // Redirect unauthenticated user to login, preserving intended path as a string
      navigate("/login", { state: { from: item.path } });
      return;
    }

    navigate(item.path);
  };

  return (
    <Paper
      elevation={0}
      sx={{
        width: "100%",
        maxWidth: 320,
        bgcolor: bgColor,
        borderRadius: 3,
        p: 1.5,
        direction: "rtl",
        mr:0,
        ml:"auto",
        borderLeft:"2px solid",
        borderColor:"grey.200",
      }}
    >
      <List component="nav" disablePadding>
        {menuItems.map((item) => {
          const isActive = location.pathname === item.path;

          return (
            <ListItemButton
              key={item.id}
              onClick={() => handleNav(item)}
              sx={{
                py: 1.2,
                px: 2,
                borderRadius: 2,
                mb: 0.5,
                justifyContent: "space-between",
                bgcolor: isActive ? "rgba(214, 90, 49, 0.08)" : "transparent",
                "&:hover": {
                  bgcolor: "rgba(214, 90, 49, 0.12)",
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  flex: 1,
                  justifyContent: "flex-start",
                }}
              >
                {item.hasBadgeDot && (
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      bgcolor: "#4a4a4a",
                      ml: -0.5,
                    }}
                  />
                )}
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    fontSize: "0.95rem",
                    fontWeight: isActive ? 700 : 500,
                    color: "text.primary",
                    textAlign: "right",
                  }}
                />
              </Box>

              <ListItemIcon
                sx={{
                  minWidth: "auto",
                  color: primaryColor,
                  "& .MuiSvgIcon-root": {
                    fontSize: "1.4rem",
                  },
                }}
              >
                {item.icon}
              </ListItemIcon>
            </ListItemButton>
          );
        })}
      </List>
    </Paper>
  );
}
